/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { useContext, useEffect, useState } from "react";
import { isStringNotEmpty, isValidGuid, objectHasPropertyString } from "utils/empty-exists";
import LinkButtonSelectOption from "./link-button-select-style";
import { colourOptions, defaultOptions, iconOptions, styleOptions } from "cms/components/components/link-button/constants";
import { Logger } from "cms/utils/log/logger";
import { ComponentLinkButtonItemContext } from "./component-link-button-context";

const LinkButtonEditor = () => {

    const componentLinkButtonContext = useContext(ComponentLinkButtonItemContext);
    const position = componentLinkButtonContext?.position;
    const item = componentLinkButtonContext?.item;
    const updateItemByPosition = componentLinkButtonContext?.updateItemByPosition;

    const _link = item?.link;
    const _colour = item?.colour;
    const _style = item?.style;
    const _icon = item?.icon;

    const [link, setLink] = useState(_link);
    const [colour, setColour] = useState(_colour);
    const [style, setStyle] = useState(_style);
    const [icon, setIcon] = useState(_icon);

    const { defaultStyle, defaultColour, defaultIcon } = defaultOptions;

    const { token } = useAuth();

    const [linkData, setLinkData] = useState(); // link object
    const [loadingLink, setLoadingLink] = useState(false);

    const setDefaultOptions = () => {
        setStyle(defaultStyle);
        setColour(defaultColour);
        setIcon(defaultIcon);
    };

    const onChangeLink = (link) => {
        setLinkData(link);
        const { guid } = link;
        setLink(guid);
        setDefaultOptions();
    };

    const onRemoveLink = () => {
        setLinkData(undefined);
        setLink(undefined);
        setDefaultOptions();
    };

    const onChangeStyle = (event) => {
        const newStyle = event?.target?.value;
        setStyle(newStyle);
    };

    const onChangeColour = (event) => {
        const newColour = event?.target?.value;
        setColour(newColour);
    };

    const onChangeIcon = (event) => {
        const newIcon = event?.target?.value;
        setIcon(newIcon);
    };

    // function to actually load link data from api
    const loadLink = async () => {
        if (isValidGuid(link)) {
            // setLoadingLink(true)
            // Logger.debug(`loading link from DB: `, component?.link)
            const _link = await SimpliCityAdminSDK.link.findOne(token, link);
            return _link;
        }
    };

    // when the component loads, use the link guid to load the link
    useEffect(() => {
        const load = async () => {
            try {
                setLoadingLink(true);
                const data = await loadLink(link);

                setLinkData(data);
            } catch (error) {
                Logger.error(error);
            } finally {
                setLoadingLink(false);
            }
        };
        // load once
        if (isStringNotEmpty(link) && !objectHasPropertyString(linkData, "guid")) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link]);

    useEffect(
        () => {
            const nextItem = structuredClone({
                ...item,
                link,
                style,
                colour,
                icon
            });
            updateItemByPosition(nextItem, position);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [link, style, colour, icon]
    );

    return (
        <div className='simplicity-editor-frame'>
            {loadingLink &&
                <>loading...</>
            }
            {!loadingLink &&
                <>
                    <LinkSelectorWrapper
                        onLinkChange={onChangeLink}
                        onLinkRemove={onRemoveLink}
                        link={linkData}
                        setLink={setLinkData}
                        loadingLink={loadingLink}
                        setLoadingLink={setLoadingLink}
                        disabled={false}
                    />
                    {link &&
                        <>
                            <LinkButtonSelectOption
                                label='Style'
                                onChange={onChangeStyle}
                                options={styleOptions}
                                value={style}
                            />
                            <LinkButtonSelectOption
                                label='Colour'
                                onChange={onChangeColour}
                                options={colourOptions}
                                value={colour}
                            />
                            <LinkButtonSelectOption
                                label='Icon'
                                onChange={onChangeIcon}
                                options={iconOptions}
                                value={icon}
                            />
                        </>
                    }
                </>
            }
        </div>
    );
};

LinkButtonEditor.displayName = "LinkButtonEditor";
export { LinkButtonEditor };
